import { ColumnDef } from '@tanstack/react-table'
import { Badge } from '@/components/ui/badge'
import { DataTableColumnHeader } from '@/components/crud'
import { Campaign } from '@/features/minigames/types'
import { GameRecord } from './schema'

export const createGameRecordColumns = (
  t: (key: string) => string,
  campaignOptions: Campaign[] = []
): ColumnDef<GameRecord>[] => [
  {
    accessorKey: 'guestName',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('gameRecords.player')} />
    ),
    cell: ({ row }) => {
      const guestName = row.getValue('guestName') as string
      const userId = row.original.userId
      const displayName = guestName || userId || t('gameRecords.guest')
      return (
        <div className='flex space-x-2'>
          <span className='max-w-[200px] truncate font-medium'>
            {displayName}
          </span>
        </div>
      )
    },
    enableSorting: true,
    enableHiding: false,
  },
  {
    accessorKey: 'guestPhone',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('gameRecords.phone')} />
    ),
    cell: ({ row }) => {
      const guestPhone = row.getValue('guestPhone') as string
      return (
        <div className='flex space-x-2'>
          <span className='max-w-[150px] truncate font-mono text-sm'>
            {guestPhone || '-'}
          </span>
        </div>
      )
    },
    enableSorting: false,
  },
  {
    accessorKey: 'playerType',
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={t('gameRecords.playerType')}
      />
    ),
    cell: ({ row }) => {
      const playerType = row.getValue('playerType') as string
      return (
        <Badge variant={playerType === 'USER' ? 'default' : 'secondary'}>
          {t(`gameRecords.playerTypes.${playerType?.toLowerCase() || 'guest'}`)}
        </Badge>
      )
    },
    filterFn: (row, id, value) => {
      return value.includes(row.getValue(id))
    },
    enableSorting: true,
  },
  {
    accessorKey: 'prize',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('gameRecords.prize')} />
    ),
    cell: ({ row }) => {
      const prize = row.getValue('prize') as GameRecord['prize']
      return (
        <div className='flex space-x-2'>
          <span className='max-w-[200px] truncate'>
            {prize?.productId || prize?.type || t('gameRecords.noPrize')}
          </span>
        </div>
      )
    },
    enableSorting: false,
  },
  {
    accessorKey: 'score',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('gameRecords.score')} />
    ),
    cell: ({ row }) => {
      const score = row.getValue('score') as number
      return (
        <div className='flex space-x-2'>
          <span className='font-mono text-sm'>{score ?? '-'}</span>
        </div>
      )
    },
    enableSorting: true,
  },
  {
    accessorKey: 'campaignId',
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={t('gameRecords.campaign')}
      />
    ),
    cell: ({ row }) => {
      const campaignId = row.getValue('campaignId') as string
      const campaign = campaignOptions.find((c) => c.id === campaignId)
      const displayName = campaign?.name || campaignId
      return (
        <div className='flex space-x-2'>
          <span className='max-w-[150px] truncate'>{displayName}</span>
        </div>
      )
    },
    filterFn: (row, id, value) => {
      return value.includes(row.getValue(id))
    },
    enableSorting: true,
  },
  {
    accessorKey: 'createdAt',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('common.createdAt')} />
    ),
    cell: ({ row }) => {
      const date = row.getValue('createdAt') as string
      return (
        <div className='flex w-[120px] items-center'>
          <span className='font-mono text-xs'>
            {date ? new Date(date).toLocaleDateString('vi-VN') : '-'}
          </span>
        </div>
      )
    },
    enableSorting: true,
  },
]
